import { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import {
  Users, ArrowLeft, Copy, Coins, Plus, Gift, HeartHandshake, MapPin, Loader2,
  Sparkles, CheckCircle2, HandCoins, Shield, Target, MessageCircle, Send, ImagePlus, Trash2, Pencil,
} from "lucide-react";
import api, { fileUrl, apiErr } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import ImageLightbox from "../components/ImageLightbox";
import { toast } from "sonner";

function timeAgo(iso) {
  const d = (Date.now() - new Date(iso).getTime()) / 1000;
  if (d < 60) return "now";
  if (d < 3600) return `${Math.floor(d / 60)}m`;
  if (d < 86400) return `${Math.floor(d / 3600)}h`;
  return `${Math.floor(d / 86400)}d`;
}

export default function NeighborGroup() {
  const { groupId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [g, setG] = useState(null);
  const [loading, setLoading] = useState(true);
  const [givers, setGivers] = useState([]);
  const [contribOpen, setContribOpen] = useState(false);
  const [initOpen, setInitOpen] = useState(false);
  const [cardOpen, setCardOpen] = useState(false);
  const [goalOpen, setGoalOpen] = useState(false);

  const load = useCallback(() => {
    api.get(`/neighbors/groups/${groupId}`).then((r) => setG(r.data)).catch((e) => { toast.error(apiErr(e)); setG(false); }).finally(() => setLoading(false));
    api.get(`/neighbors/groups/${groupId}/contributions`).then((r) => setGivers(r.data)).catch(() => {});
  }, [groupId]);
  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="min-h-[50vh] flex items-center justify-center text-copper"><Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading group…</div>;
  if (!g) return <div className="max-w-md mx-auto text-center py-20 px-4"><p className="text-muted-foreground">You don't have access to this group.</p><Button onClick={() => navigate("/neighbors")} variant="outline" className="rounded-full mt-4">Back to Neighbors</Button></div>;

  const canManage = g.my_role === "organizer" || g.my_role === "admin";
  const copyCode = () => { navigator.clipboard?.writeText(g.join_code); toast.success("Join code copied — share it with your neighbors!"); };
  const goal = g.fund_goal || 0;
  const pct = goal > 0 ? Math.min(100, Math.round(((g.fund_balance || 0) / goal) * 100)) : 0;

  return (
    <div data-testid="group-detail-page" className="max-w-3xl mx-auto px-3.5 sm:px-6 py-5 sm:py-8 flex flex-col items-center">
      <button onClick={() => navigate("/neighbors")} className="self-start text-sm text-muted-foreground hover:text-copper flex items-center gap-1 mb-3"><ArrowLeft className="w-4 h-4" /> Neighbors</button>

      {/* Centered header */}
      <div className="w-full bg-card border border-border rounded-3xl p-5 sm:p-7 flex flex-col items-center text-center">
        <div className="w-14 h-14 rounded-2xl bg-copper/15 text-copper flex items-center justify-center"><Users className="w-7 h-7" /></div>
        <h1 className="font-serif text-2xl sm:text-3xl font-bold text-bronze break-words mt-3">{g.name}</h1>
        <p className="text-xs text-muted-foreground flex items-center justify-center gap-2 flex-wrap mt-1">
          {g.area && <span className="flex items-center gap-1"><MapPin className="w-3 h-3" /> {g.area}</span>}
          <span>· {g.member_count} member{g.member_count === 1 ? "" : "s"}</span>
          {g.my_role === "admin" && <span className="text-copper font-semibold flex items-center gap-1"><Shield className="w-3 h-3" /> admin view</span>}
        </p>
        {g.description && <p className="text-sm text-foreground/80 mt-3 break-words whitespace-pre-wrap max-w-xl">{g.description}</p>}
        <button data-testid="copy-join-code" onClick={copyCode} className="mt-4 inline-flex items-center gap-2 bg-secondary hover:bg-accent rounded-full pl-3 pr-2 py-1.5 text-sm">
          <span className="text-muted-foreground text-xs">Invite code</span>
          <span className="font-mono font-bold tracking-widest text-bronze">{g.join_code}</span>
          <Copy className="w-3.5 h-3.5 text-copper" />
        </button>
      </div>

      {/* Centered contribution wall */}
      <div data-testid="contribution-wall" className="w-full max-w-lg mx-auto bg-gradient-to-br from-penny/12 to-copper/10 border border-penny/25 rounded-3xl p-6 sm:p-8 text-center space-y-3 mt-4">
        <p className="font-mono text-xs uppercase tracking-widest text-copper font-bold flex items-center justify-center gap-1.5"><Coins className="w-3.5 h-3.5" /> Group fund</p>
        <p data-testid="group-fund-balance" className="font-serif text-4xl font-extrabold text-bronze">${(g.fund_balance || 0).toFixed(2)}</p>
        {goal > 0 ? (
          <>
            <div className="w-full h-4 bg-white/70 rounded-full overflow-hidden p-0.5 border border-border/60 mx-auto max-w-sm">
              <div className="h-full bg-gradient-to-r from-copper via-penny to-sage rounded-full transition-all duration-500" style={{ width: `${pct}%` }} data-testid="fund-thermometer" />
            </div>
            <p className="text-sm text-foreground/70 font-medium">${(g.fund_balance || 0).toFixed(0)} of ${goal.toFixed(0)} goal · {pct}%</p>
          </>
        ) : (
          <p className="text-sm text-muted-foreground">{canManage ? "Set a goal to show a progress thermometer." : "Every dollar helps a neighbor."}</p>
        )}
        <div className="flex flex-col sm:flex-row items-center justify-center gap-2.5 max-w-md mx-auto pt-1 [&>*]:flex-1 w-full">
          <Button data-testid="contribute-btn" onClick={() => setContribOpen(true)} className="w-full rounded-full bg-copper hover:bg-copper/90 text-white min-h-[48px]"><HandCoins className="w-4 h-4 mr-1.5" /> Chip in</Button>
          {canManage && <Button data-testid="set-goal-btn" onClick={() => setGoalOpen(true)} variant="outline" className="w-full rounded-full min-h-[48px] border-copper/40 text-copper"><Target className="w-4 h-4 mr-1.5" /> {goal > 0 ? "Edit goal" : "Set goal"}</Button>}
        </div>

        {givers.length > 0 && (
          <div className="pt-3 mt-2 border-t border-border/40">
            <p className="text-xs font-mono uppercase tracking-widest text-muted-foreground mb-2">Recent givers 💛</p>
            <div className="flex flex-col items-center gap-1.5 max-w-sm mx-auto" data-testid="recent-givers">
              {givers.slice(0, 6).map((r) => (
                <div key={r.id} className="flex items-center justify-center gap-2 text-sm">
                  <img src={fileUrl(r.from?.avatar_url) || `https://api.dicebear.com/7.x/lorelei/svg?seed=${r.from?.user_id}`} alt="" className="w-6 h-6 rounded-full bg-secondary object-cover" />
                  <span className="font-medium text-bronze">{r.from?.name || "Someone"}</span>
                  <span className="text-copper font-mono font-semibold">${(r.amount || 0).toFixed(2)}</span>
                  <span className="text-muted-foreground text-xs">· {timeAgo(r.created_at)}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <Tabs defaultValue="initiatives" className="mt-6 w-full flex flex-col items-center">
        <TabsList className="rounded-full bg-secondary p-1 flex-wrap h-auto justify-center">
          <TabsTrigger value="initiatives" data-testid="tab-initiatives" className="rounded-full">Initiatives ({g.initiatives.length})</TabsTrigger>
          <TabsTrigger value="chat" data-testid="tab-chat" className="rounded-full">Chat</TabsTrigger>
          <TabsTrigger value="photos" data-testid="tab-photos" className="rounded-full">Photos</TabsTrigger>
          <TabsTrigger value="giftcards" data-testid="tab-giftcards" className="rounded-full">Gift cards ({g.giftcards.length})</TabsTrigger>
          <TabsTrigger value="members" data-testid="tab-members" className="rounded-full">Members ({g.member_count})</TabsTrigger>
        </TabsList>

        <TabsContent value="initiatives" className="mt-4 w-full max-w-2xl mx-auto flex flex-col items-center space-y-3">
          <Button data-testid="new-initiative-btn" onClick={() => setInitOpen(true)} variant="outline" className="rounded-full"><Plus className="w-4 h-4 mr-1.5" /> Start an initiative</Button>
          {g.initiatives.length === 0 ? (
            <EmptyState icon={Sparkles} title="No initiatives yet" text="Nominate a neighbor to help, or start a fund for a block party, Easter egg hunt or back-to-school shopping. 💛" />
          ) : g.initiatives.map((ini) => (
            <InitiativeCard key={ini.initiative_id} ini={ini} canManage={canManage} fundBalance={g.fund_balance} onChange={load} />
          ))}
        </TabsContent>

        <TabsContent value="chat" className="mt-4 w-full max-w-xl mx-auto">
          <GroupChat groupId={groupId} me={user} />
        </TabsContent>

        <TabsContent value="photos" className="mt-4 w-full max-w-2xl mx-auto">
          <GroupPhotos groupId={groupId} me={user} canManage={canManage} />
        </TabsContent>

        <TabsContent value="giftcards" className="mt-4 w-full max-w-2xl mx-auto flex flex-col items-center space-y-3">
          {canManage && <Button data-testid="new-giftcard-btn" onClick={() => setCardOpen(true)} variant="outline" className="rounded-full"><Plus className="w-4 h-4 mr-1.5" /> Issue a gift card</Button>}
          {g.giftcards.length === 0 ? (
            <EmptyState icon={Gift} title="No gift cards yet" text={canManage ? "Load funds from the pot onto a reusable card and send it to a neighbor." : "Only organizers can issue gift cards."} />
          ) : g.giftcards.map((c) => (
            <div key={c.card_id} data-testid={`giftcard-${c.card_id}`} className="w-full bg-card border border-border rounded-2xl p-4 flex items-center justify-between gap-3 text-left">
              <div className="min-w-0">
                <p className="font-medium text-bronze flex items-center gap-1.5"><Gift className="w-4 h-4 text-sage" /> {c.label}</p>
                <p className="text-xs font-mono text-muted-foreground mt-0.5">{c.code}</p>
                <p className="text-xs text-foreground/60 mt-0.5 break-words">{c.recipient_email ? `For ${c.recipient_email}` : "Unassigned"}{c.note ? ` · ${c.note}` : ""}</p>
              </div>
              <div className="text-right shrink-0">
                <p className="font-mono font-bold text-sage">${(c.balance || 0).toFixed(2)}</p>
                <span className={`text-[10px] uppercase font-semibold px-2 py-0.5 rounded-full ${c.status === "redeemed" ? "bg-secondary text-muted-foreground" : "bg-sage/20 text-sage"}`}>{c.status}</span>
              </div>
            </div>
          ))}
        </TabsContent>

        <TabsContent value="members" className="mt-4 w-full max-w-xl mx-auto">
          <div className="space-y-2" data-testid="members-list">
            {g.members.map((m) => (
              <Link key={m.user_id} to={`/u/${m.user_id}`} className="flex items-center gap-3 bg-card border border-border rounded-2xl p-3 hover:border-copper/40">
                <img src={fileUrl(m.avatar_url) || `https://api.dicebear.com/7.x/lorelei/svg?seed=${m.user_id}`} alt="" className="w-9 h-9 rounded-full bg-secondary object-cover" />
                <span className="text-sm font-medium text-bronze truncate">{m.name}</span>
                {g.organizer_ids?.includes(m.user_id) && <span className="text-[10px] uppercase font-semibold text-copper bg-copper/10 px-2 py-0.5 rounded-full ml-auto">organizer</span>}
              </Link>
            ))}
          </div>
        </TabsContent>
      </Tabs>

      <ContributeDialog open={contribOpen} onClose={() => setContribOpen(false)} groupId={groupId} groupName={g.name} />
      <InitiativeDialog open={initOpen} onClose={() => setInitOpen(false)} groupId={groupId} onCreated={() => { setInitOpen(false); load(); }} />
      <GiftCardDialog open={cardOpen} onClose={() => setCardOpen(false)} groupId={groupId} fundBalance={g.fund_balance} onCreated={() => { setCardOpen(false); load(); }} />
      <GoalDialog open={goalOpen} onClose={() => setGoalOpen(false)} groupId={groupId} current={goal} onSaved={() => { setGoalOpen(false); load(); }} />
    </div>
  );
}

function EmptyState({ icon: Icon, title, text }) {
  return (
    <div className="flex flex-col items-center justify-center text-center p-8 sm:p-10 rounded-3xl bg-cream/40 border-2 border-dashed border-border/70 max-w-md mx-auto space-y-2.5">
      <div className="w-14 h-14 rounded-full bg-white flex items-center justify-center text-copper shadow-sm border border-border/60"><Icon className="w-6 h-6" /></div>
      <p className="font-serif text-lg font-semibold text-bronze">{title}</p>
      <p className="text-sm text-muted-foreground max-w-xs">{text}</p>
    </div>
  );
}

function GroupChat({ groupId, me }) {
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [lbImg, setLbImg] = useState(null);
  const endRef = useRef();
  const fileRef = useRef();
  const load = useCallback(() => {
    api.get(`/neighbors/groups/${groupId}/chat`).then((r) => setMsgs(r.data)).catch(() => {});
  }, [groupId]);
  useEffect(() => { load(); const t = setInterval(load, 3000); return () => clearInterval(t); }, [load]);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs.length]);

  const send = async (imageUrl) => {
    const t = text.trim();
    if (!t && !imageUrl) return;
    setSending(true); if (!imageUrl) setText("");
    try { await api.post(`/neighbors/groups/${groupId}/chat`, { text: imageUrl ? text : t, image_url: imageUrl || null }); if (imageUrl) setText(""); load(); }
    catch (e) { toast.error(apiErr(e)); if (!imageUrl) setText(t); }
    setSending(false);
  };

  const attach = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", f);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await send(data.url);
    } catch (err) { toast.error(apiErr(err)); }
    setUploading(false); e.target.value = "";
  };

  return (
    <div className="w-full flex flex-col items-center">
      <p className="font-mono text-xs uppercase tracking-widest text-copper font-bold flex items-center justify-center gap-1.5 mb-3"><MessageCircle className="w-3.5 h-3.5" /> Private neighbor thread</p>
      <div data-testid="group-chat-messages" className="w-full bg-card border border-border rounded-3xl p-3 sm:p-4 h-[52vh] overflow-y-auto space-y-3">
        {msgs.length === 0 ? (
          <div className="h-full flex items-center justify-center text-center text-sm text-muted-foreground px-6">Start the conversation — plan your next event or check in on a neighbor. 💛</div>
        ) : msgs.map((m) => {
          const mine = m.user_id === me?.user_id;
          return (
            <div key={m.id} data-testid={`chat-msg-${m.id}`} className={`flex items-end gap-2 ${mine ? "flex-row-reverse" : ""}`}>
              <img src={fileUrl(m.avatar_url) || `https://api.dicebear.com/7.x/lorelei/svg?seed=${m.user_id}`} alt="" className="w-7 h-7 rounded-full bg-secondary object-cover shrink-0" />
              <div className={`max-w-[75%] ${mine ? "items-end" : "items-start"} flex flex-col`}>
                {m.image_url && (
                  <img data-testid={`chat-img-${m.id}`} src={fileUrl(m.image_url)} alt="" onClick={() => setLbImg(m.image_url)}
                    className="rounded-2xl max-h-56 w-auto object-cover cursor-zoom-in border border-border mb-1" />
                )}
                {m.text && <div className={`px-3.5 py-2 rounded-2xl text-sm break-words ${mine ? "bg-copper text-white rounded-br-md" : "bg-secondary text-foreground rounded-bl-md"}`}>{m.text}</div>}
                <span className="text-[10px] text-muted-foreground mt-0.5 px-1">{mine ? "You" : m.name} · {timeAgo(m.created_at)}</span>
              </div>
            </div>
          );
        })}
        <div ref={endRef} />
      </div>
      <div className="w-full flex items-center gap-2 mt-3">
        <input ref={fileRef} type="file" accept="image/*" hidden onChange={attach} data-testid="chat-file-input" />
        <Button data-testid="chat-attach" onClick={() => fileRef.current?.click()} disabled={uploading} variant="outline" className="rounded-full shrink-0 w-11 h-11 p-0">
          {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ImagePlus className="w-4 h-4" />}
        </Button>
        <Input data-testid="chat-input" value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()} placeholder="Message your neighbors…" className="rounded-full flex-1" />
        <Button data-testid="chat-send" onClick={() => send()} disabled={sending || uploading || !text.trim()} className="rounded-full bg-copper hover:bg-copper/90 text-white shrink-0 w-11 h-11 p-0"><Send className="w-4 h-4" /></Button>
      </div>
      {lbImg && <ImageLightbox src={lbImg} onClose={() => setLbImg(null)} />}
    </div>
  );
}

function GroupPhotos({ groupId, me, canManage }) {
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lightbox, setLightbox] = useState(null);
  const [uploadFiles, setUploadFiles] = useState(null); // [{file, url, caption, uploading}]
  const [saving, setSaving] = useState(false);
  const [editPhoto, setEditPhoto] = useState(null);
  const fileRef = useRef();

  const load = useCallback(() => {
    api.get(`/neighbors/groups/${groupId}/photos`).then((r) => setPhotos(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, [groupId]);
  useEffect(() => { load(); }, [load]);

  const pick = (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploadFiles(files.map((f) => ({ file: f, preview: URL.createObjectURL(f), caption: "" })));
    e.target.value = "";
  };

  const confirmUpload = async () => {
    setSaving(true);
    try {
      for (const item of uploadFiles) {
        const fd = new FormData(); fd.append("file", item.file);
        const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        await api.post(`/neighbors/groups/${groupId}/photos`, { url: data.url, caption: item.caption });
      }
      toast.success("Added to the album 💛"); setUploadFiles(null); load();
    } catch (err) { toast.error(apiErr(err)); }
    setSaving(false);
  };

  const del = async (photoId) => {
    if (!window.confirm("Remove this photo from the album?")) return;
    try { await api.delete(`/neighbors/photos/${photoId}`); setPhotos((prev) => prev.filter((p) => p.photo_id !== photoId)); toast.success("Removed"); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const urls = photos.map((p) => p.url);
  const caps = photos.map((p) => p.caption || "");

  return (
    <div className="w-full flex flex-col items-center">
      <p className="font-mono text-xs uppercase tracking-widest text-copper font-bold flex items-center justify-center gap-1.5 mb-3"><ImagePlus className="w-3.5 h-3.5" /> Group album — the good we did together</p>
      <input ref={fileRef} type="file" accept="image/*" multiple hidden onChange={pick} data-testid="album-file-input" />
      <Button data-testid="album-upload-btn" onClick={() => fileRef.current?.click()} variant="outline" className="rounded-full mb-4">
        <ImagePlus className="w-4 h-4 mr-1.5" /> Add photos
      </Button>

      {loading ? (
        <div className="py-10 text-muted-foreground flex items-center"><Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading…</div>
      ) : photos.length === 0 ? (
        <EmptyState icon={ImagePlus} title="No photos yet" text="Share pictures from your events so everyone can remember the good you did together." />
      ) : (
        <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 w-full" data-testid="album-grid">
          {photos.map((p, i) => (
            <div key={p.photo_id} className="relative group aspect-square rounded-xl overflow-hidden bg-secondary">
              <button data-testid={`album-photo-${i}`} onClick={() => setLightbox(i)} className="w-full h-full">
                <img src={fileUrl(p.url)} alt={p.caption || ""} className="w-full h-full object-cover hover:opacity-95 transition-opacity" />
              </button>
              {p.caption && <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-black/70 to-transparent text-white text-[10px] px-1.5 py-1 truncate pointer-events-none">{p.caption}</div>}
              {(canManage || p.user_id === me?.user_id) && (
                <div className="absolute top-1 right-1 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button data-testid={`album-edit-${p.photo_id}`} onClick={() => setEditPhoto(p)} className="bg-black/60 text-white rounded-full p-1.5 hover:bg-black/80"><Pencil className="w-3.5 h-3.5" /></button>
                  <button data-testid={`album-delete-${p.photo_id}`} onClick={() => del(p.photo_id)} className="bg-black/60 text-white rounded-full p-1.5 hover:bg-black/80"><Trash2 className="w-3.5 h-3.5" /></button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
      {lightbox !== null && urls.length > 0 && (
        <ImageLightbox images={urls} captions={caps} startIndex={lightbox} onClose={() => setLightbox(null)} />
      )}

      {/* Upload with captions */}
      <Dialog open={!!uploadFiles} onOpenChange={(o) => !o && setUploadFiles(null)}>
        <DialogContent data-testid="album-upload-dialog" className="sm:max-w-md rounded-3xl max-h-[90vh] overflow-y-auto text-center">
          <DialogHeader><DialogTitle className="font-serif text-xl text-bronze text-center">Add a caption</DialogTitle><DialogDescription className="sr-only">Add optional captions to your photos before uploading.</DialogDescription></DialogHeader>
          <div className="space-y-3 text-left">
            {uploadFiles?.map((item, i) => (
              <div key={i} className="flex gap-3 items-center">
                <img src={item.preview} alt="" className="w-16 h-16 rounded-xl object-cover bg-secondary shrink-0" />
                <Input data-testid={`album-caption-input-${i}`} value={item.caption} onChange={(e) => setUploadFiles((prev) => prev.map((x, idx) => idx === i ? { ...x, caption: e.target.value } : x))} placeholder="Caption (optional)" className="rounded-xl" />
              </div>
            ))}
          </div>
          <DialogFooter className="grid grid-cols-2 gap-2.5">
            <Button variant="outline" onClick={() => setUploadFiles(null)} className="rounded-full">Cancel</Button>
            <Button data-testid="album-upload-confirm" onClick={confirmUpload} disabled={saving} className="rounded-full bg-copper hover:bg-copper/90 text-white">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : `Add ${uploadFiles?.length || ""} photo${uploadFiles?.length === 1 ? "" : "s"}`}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <CaptionEditDialog photo={editPhoto} onClose={() => setEditPhoto(null)} onSaved={() => { setEditPhoto(null); load(); }} />
    </div>
  );
}

function CaptionEditDialog({ photo, onClose, onSaved }) {
  const [cap, setCap] = useState(""); const [busy, setBusy] = useState(false);
  useEffect(() => { setCap(photo?.caption || ""); }, [photo]);
  const save = async () => {
    setBusy(true);
    try { await api.patch(`/neighbors/photos/${photo.photo_id}`, { caption: cap }); toast.success("Caption saved 💛"); onSaved(); }
    catch (e) { toast.error(apiErr(e)); }
    setBusy(false);
  };
  return (
    <Dialog open={!!photo} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="caption-edit-dialog" className="sm:max-w-sm rounded-3xl text-center flex flex-col items-center">
        <DialogHeader><DialogTitle className="font-serif text-lg text-bronze text-center">Edit caption</DialogTitle><DialogDescription className="sr-only">Edit this photo's caption.</DialogDescription></DialogHeader>
        {photo && <img src={fileUrl(photo.url)} alt="" className="w-full max-h-48 object-contain rounded-xl bg-secondary" />}
        <Input data-testid="caption-edit-input" value={cap} onChange={(e) => setCap(e.target.value)} placeholder="Caption" className="rounded-xl mt-1" />
        <DialogFooter className="grid grid-cols-2 gap-2.5 w-full">
          <Button variant="outline" onClick={onClose} className="rounded-full">Cancel</Button>
          <Button data-testid="caption-edit-save" onClick={save} disabled={busy} className="rounded-full bg-copper hover:bg-copper/90 text-white">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function InitiativeCard({ ini, canManage, fundBalance, onChange }) {
  const [allocOpen, setAllocOpen] = useState(false);
  const [amount, setAmount] = useState("");
  const [note] = useState("");
  const [busy, setBusy] = useState(false);
  const pct = ini.goal_amount > 0 ? Math.min(100, Math.round((ini.raised_amount / ini.goal_amount) * 100)) : 0;
  const distributed = ini.status === "distributed";

  const allocate = async () => {
    const amt = parseFloat(amount);
    if (!amt || amt <= 0) { toast.error("Enter an amount."); return; }
    setBusy(true);
    try { await api.post(`/neighbors/initiatives/${ini.initiative_id}/fund`, { amount: amt }); toast.success("Allocated from the pot 💛"); setAllocOpen(false); setAmount(""); onChange(); }
    catch (e) { toast.error(apiErr(e)); }
    setBusy(false);
  };
  const distribute = async () => {
    if (!window.confirm("Mark this as given? If a Haplap member is the beneficiary, the allocated funds go to their wallet now.")) return;
    setBusy(true);
    try { await api.post(`/neighbors/initiatives/${ini.initiative_id}/distribute`, { note }); toast.success("Marked as given 🎉"); onChange(); }
    catch (e) { toast.error(apiErr(e)); }
    setBusy(false);
  };

  return (
    <div data-testid={`initiative-${ini.initiative_id}`} className="w-full bg-card border border-border rounded-2xl p-4 sm:p-5 text-center flex flex-col items-center">
      <div className="flex items-center justify-center gap-2 flex-wrap">
        <span className={`text-[10px] uppercase font-mono font-bold px-2 py-0.5 rounded-full ${ini.kind === "beneficiary" ? "bg-copper/15 text-copper" : "bg-penny/20 text-bronze"}`}>{ini.kind === "beneficiary" ? "neighbor" : "program"}</span>
        <h3 className="font-serif text-lg font-semibold text-bronze break-words">{ini.title}</h3>
        {distributed && <span className="text-[10px] uppercase font-semibold text-sage bg-sage/15 px-2 py-0.5 rounded-full flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> given</span>}
      </div>
      {ini.beneficiary_name && <p className="text-xs text-muted-foreground mt-0.5">For {ini.beneficiary_name}</p>}
      {ini.story && <p className="text-sm text-foreground/80 mt-1.5 break-words whitespace-pre-wrap max-w-lg">{ini.story}</p>}

      {ini.goal_amount > 0 && (
        <div className="mt-3 w-full max-w-sm">
          <div className="flex justify-between text-xs mb-1"><span className="text-muted-foreground">Raised</span><span className="font-semibold text-bronze">${(ini.raised_amount || 0).toFixed(2)} / ${ini.goal_amount.toFixed(2)}</span></div>
          <div className="h-2.5 bg-secondary rounded-full overflow-hidden"><div className="h-full bg-sage rounded-full transition-all" style={{ width: `${pct}%` }} /></div>
        </div>
      )}
      {ini.goal_amount === 0 && ini.raised_amount > 0 && <p className="text-sm font-semibold text-bronze mt-2">Allocated: ${ini.raised_amount.toFixed(2)}</p>}
      {distributed && ini.distributed_note && <p className="text-xs text-sage mt-2 italic">"{ini.distributed_note}"</p>}

      {canManage && !distributed && (
        <div className="grid grid-cols-2 gap-2 mt-3 w-full max-w-xs">
          <Button data-testid={`allocate-${ini.initiative_id}`} onClick={() => setAllocOpen(true)} variant="outline" size="sm" className="rounded-full"><Coins className="w-3.5 h-3.5 mr-1" /> Allocate</Button>
          <Button data-testid={`distribute-${ini.initiative_id}`} onClick={distribute} disabled={busy} size="sm" className="rounded-full bg-sage hover:bg-sage/90 text-white"><Sparkles className="w-3.5 h-3.5 mr-1" /> Mark given</Button>
        </div>
      )}

      <Dialog open={allocOpen} onOpenChange={(o) => !o && setAllocOpen(false)}>
        <DialogContent className="sm:max-w-sm rounded-3xl text-center">
          <DialogHeader><DialogTitle className="font-serif text-lg text-bronze text-center">Allocate from the pot</DialogTitle><DialogDescription className="sr-only">Move money from the group pot to this initiative.</DialogDescription></DialogHeader>
          <p className="text-xs text-muted-foreground">Available in pot: <b className="text-bronze">${(fundBalance || 0).toFixed(2)}</b></p>
          <div className="relative max-w-[200px] mx-auto w-full"><span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
            <Input data-testid={`allocate-amount-${ini.initiative_id}`} type="number" min="1" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="Amount" className="pl-7 rounded-xl text-center" /></div>
          <DialogFooter className="grid grid-cols-2 gap-2.5">
            <Button variant="outline" onClick={() => setAllocOpen(false)} className="rounded-full">Cancel</Button>
            <Button data-testid={`allocate-submit-${ini.initiative_id}`} onClick={allocate} disabled={busy} className="rounded-full bg-copper hover:bg-copper/90 text-white">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Allocate"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ContributeDialog({ open, onClose, groupId, groupName }) {
  const [amount, setAmount] = useState(10); const [busy, setBusy] = useState(false);
  const go = async () => {
    if (!amount || amount < 1) { toast.error("Minimum is $1."); return; }
    setBusy(true);
    try { const { data } = await api.post(`/neighbors/groups/${groupId}/contribute`, { amount: parseFloat(amount), origin_url: window.location.origin }); window.location.href = data.checkout_url; }
    catch (e) { toast.error(apiErr(e)); setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="contribute-dialog" className="sm:max-w-sm rounded-3xl text-center flex flex-col items-center">
        <DialogHeader><DialogTitle className="font-serif text-xl text-bronze flex items-center justify-center gap-2 text-center"><HandCoins className="w-5 h-5 text-copper" /> Chip in</DialogTitle><DialogDescription className="sr-only">Contribute to {groupName}'s fund.</DialogDescription></DialogHeader>
        <p className="text-sm text-muted-foreground">Add to <b className="text-bronze">{groupName}</b>'s shared pot.</p>
        <div className="flex gap-2 my-2 w-full max-w-xs mx-auto">
          {[10, 25, 50, 100].map((v) => (
            <button key={v} data-testid={`contrib-preset-${v}`} onClick={() => setAmount(v)} className={`flex-1 py-2 rounded-xl text-sm font-semibold ${Number(amount) === v ? "bg-copper text-white" : "bg-secondary text-bronze"}`}>${v}</button>
          ))}
        </div>
        <div className="relative max-w-[200px] mx-auto w-full"><span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
          <Input data-testid="contrib-amount" type="number" min="1" value={amount} onChange={(e) => setAmount(e.target.value)} className="pl-7 rounded-xl text-center" /></div>
        <p className="text-[11px] text-muted-foreground mt-1 max-w-xs">Secure checkout (Stripe test mode). You cover a small platform + processing fee; 100% of your gift lands in the pot.</p>
        <DialogFooter className="grid grid-cols-2 gap-2.5 w-full">
          <Button variant="outline" onClick={onClose} className="rounded-full">Cancel</Button>
          <Button data-testid="contrib-submit" onClick={go} disabled={busy} className="rounded-full bg-copper hover:bg-copper/90 text-white">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Continue"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function GoalDialog({ open, onClose, groupId, current, onSaved }) {
  const [goal, setGoal] = useState(current || ""); const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setGoal(current || ""); }, [open, current]);
  const save = async () => {
    setBusy(true);
    try { await api.patch(`/neighbors/groups/${groupId}`, { fund_goal: parseFloat(goal) || 0 }); toast.success("Goal updated 💛"); onSaved(); }
    catch (e) { toast.error(apiErr(e)); }
    setBusy(false);
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="goal-dialog" className="sm:max-w-sm rounded-3xl text-center flex flex-col items-center">
        <DialogHeader><DialogTitle className="font-serif text-xl text-bronze flex items-center justify-center gap-2 text-center"><Target className="w-5 h-5 text-copper" /> Fund goal</DialogTitle><DialogDescription className="sr-only">Set a fundraising goal for the group thermometer.</DialogDescription></DialogHeader>
        <p className="text-sm text-muted-foreground">Set a target to show a progress thermometer to everyone.</p>
        <div className="relative max-w-[200px] mx-auto w-full"><span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
          <Input data-testid="goal-amount" type="number" min="0" value={goal} onChange={(e) => setGoal(e.target.value)} placeholder="e.g. 500" className="pl-7 rounded-xl text-center" /></div>
        <DialogFooter className="grid grid-cols-2 gap-2.5 w-full">
          <Button variant="outline" onClick={onClose} className="rounded-full">Cancel</Button>
          <Button data-testid="goal-submit" onClick={save} disabled={busy} className="rounded-full bg-copper hover:bg-copper/90 text-white">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save goal"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function InitiativeDialog({ open, onClose, groupId, onCreated }) {
  const [kind, setKind] = useState("program");
  const [title, setTitle] = useState(""); const [story, setStory] = useState(""); const [goal, setGoal] = useState(""); const [beneficiary, setBeneficiary] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) { setKind("program"); setTitle(""); setStory(""); setGoal(""); setBeneficiary(""); } }, [open]);
  const create = async () => {
    if (!title.trim()) { toast.error("Give it a title."); return; }
    setBusy(true);
    try {
      await api.post(`/neighbors/groups/${groupId}/initiatives`, {
        kind, title, story, goal_amount: parseFloat(goal) || 0,
        beneficiary_name: kind === "beneficiary" ? beneficiary : null,
      });
      toast.success("Initiative started 💛"); onCreated();
    } catch (e) { toast.error(apiErr(e)); }
    setBusy(false);
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="initiative-dialog" className="sm:max-w-md rounded-3xl max-h-[90vh] overflow-y-auto text-center">
        <DialogHeader><DialogTitle className="font-serif text-xl text-bronze text-center">Start an initiative</DialogTitle><DialogDescription className="sr-only">Create a beneficiary or program initiative.</DialogDescription></DialogHeader>
        <div className="grid grid-cols-2 gap-2">
          <button data-testid="init-kind-program" onClick={() => setKind("program")} className={`p-3 rounded-2xl border text-center flex flex-col items-center ${kind === "program" ? "border-copper bg-copper/10" : "border-border"}`}>
            <Sparkles className="w-4 h-4 text-penny" /><p className="font-medium text-sm mt-1 text-bronze">Program / event</p><p className="text-[11px] text-muted-foreground">Block party, egg hunt, school supplies, playground</p>
          </button>
          <button data-testid="init-kind-beneficiary" onClick={() => setKind("beneficiary")} className={`p-3 rounded-2xl border text-center flex flex-col items-center ${kind === "beneficiary" ? "border-copper bg-copper/10" : "border-border"}`}>
            <HeartHandshake className="w-4 h-4 text-copper" /><p className="font-medium text-sm mt-1 text-bronze">Help a neighbor</p><p className="text-[11px] text-muted-foreground">Nominate a family or person to lift up</p>
          </button>
        </div>
        <div className="space-y-3 mt-1 text-left">
          <div><Label>Title</Label><Input data-testid="init-title" value={title} onChange={(e) => setTitle(e.target.value)} placeholder={kind === "beneficiary" ? "Groceries for the Rivera family" : "Neighborhood Easter egg hunt"} className="rounded-xl mt-1" /></div>
          {kind === "beneficiary" && <div><Label>Who is this for? <span className="text-muted-foreground font-normal">(optional)</span></Label><Input data-testid="init-beneficiary" value={beneficiary} onChange={(e) => setBeneficiary(e.target.value)} placeholder="A neighbor's name" className="rounded-xl mt-1" /></div>}
          <div><Label>Tell the group about it</Label><Textarea data-testid="init-story" value={story} onChange={(e) => setStory(e.target.value)} rows={3} className="rounded-xl mt-1" /></div>
          <div><Label>Goal <span className="text-muted-foreground font-normal">(optional)</span></Label>
            <div className="relative mt-1"><span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
              <Input data-testid="init-goal" type="number" min="0" value={goal} onChange={(e) => setGoal(e.target.value)} placeholder="Fundraising goal" className="pl-7 rounded-xl" /></div>
          </div>
        </div>
        <DialogFooter className="grid grid-cols-2 gap-2.5">
          <Button variant="outline" onClick={onClose} className="rounded-full">Cancel</Button>
          <Button data-testid="init-submit" onClick={create} disabled={busy} className="rounded-full bg-copper hover:bg-copper/90 text-white">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Start"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function GiftCardDialog({ open, onClose, groupId, fundBalance, onCreated }) {
  const [label, setLabel] = useState(""); const [amount, setAmount] = useState(""); const [email, setEmail] = useState(""); const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) { setLabel(""); setAmount(""); setEmail(""); setNote(""); } }, [open]);
  const create = async () => {
    const amt = parseFloat(amount);
    if (!amt || amt <= 0) { toast.error("Enter a card amount."); return; }
    setBusy(true);
    try { await api.post(`/neighbors/groups/${groupId}/giftcards`, { label, amount: amt, recipient_email: email, note }); toast.success("Gift card issued 💛"); onCreated(); }
    catch (e) { toast.error(apiErr(e)); }
    setBusy(false);
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="giftcard-dialog" className="sm:max-w-md rounded-3xl max-h-[90vh] overflow-y-auto text-center">
        <DialogHeader><DialogTitle className="font-serif text-xl text-bronze flex items-center justify-center gap-2 text-center"><Gift className="w-5 h-5 text-sage" /> Issue a gift card</DialogTitle><DialogDescription className="sr-only">Load funds from the pot onto a reusable gift card.</DialogDescription></DialogHeader>
        <p className="text-xs text-muted-foreground">Loaded from the pot — available: <b className="text-bronze">${(fundBalance || 0).toFixed(2)}</b></p>
        <div className="space-y-3 mt-1 text-left">
          <div><Label>Card name</Label><Input data-testid="card-label" value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Back-to-school card" className="rounded-xl mt-1" /></div>
          <div><Label>Amount</Label><div className="relative mt-1"><span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
            <Input data-testid="card-amount" type="number" min="1" value={amount} onChange={(e) => setAmount(e.target.value)} className="pl-7 rounded-xl" /></div></div>
          <div><Label>Recipient email <span className="text-muted-foreground font-normal">(optional)</span></Label><Input data-testid="card-email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="neighbor@email.com" className="rounded-xl mt-1" /><p className="text-[11px] text-muted-foreground mt-1">If they're on Haplap, it'll appear in their Neighbors page to redeem.</p></div>
          <div><Label>Note <span className="text-muted-foreground font-normal">(optional)</span></Label><Input data-testid="card-note" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Happy holidays from the block!" className="rounded-xl mt-1" /></div>
        </div>
        <DialogFooter className="grid grid-cols-2 gap-2.5">
          <Button variant="outline" onClick={onClose} className="rounded-full">Cancel</Button>
          <Button data-testid="card-submit" onClick={create} disabled={busy} className="rounded-full bg-sage hover:bg-sage/90 text-white">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Issue card"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
